// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ============================================================
   Navbar.css — Shared KinetiQ Navigation Bar
   Covers all page variants:
     • Landing page  (full nav links + Launch App CTA)
     • Auth pages    (login / register — logo + ghost link)
     • App / Dashboard (logo + profile dropdown — replaces MainLayout topbar)
   ============================================================ */

/* ── Design tokens ─────────────────────────────────────────── */
:root {
  --kinetic-blue:   #1A3C6E;
  --kinetic-teal:   #0B8DA6;
  --kinetic-accent: #00C2A8;
  --kinetic-light:  #F0F7FA;
  --kinetic-white:  #FFFFFF;
  --kinetic-dark:   #0D1F35;
  --kinetic-gray:   #6B7A8D;
  --kinetic-border: #D6E4EC;

  --font-body: 'Inter', 'Segoe UI', sans-serif;

  --radius-sm: 6px;
  --radius-md: 12px;
  --radius-lg: 20px;
}

/* ════════════════════════════════════════════════════════════
   BASE NAV
   position:fixed is used (not sticky) because auth pages set
   overflow:hidden on ancestor elements, which breaks sticky.
   ════════════════════════════════════════════════════════════ */
.kq-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: var(--kinetic-white);
  border-bottom: 1px solid var(--kinetic-border);
  box-shadow: 0 2px 12px rgba(26, 60, 110, 0.06);
  font-family: var(--font-body);
  transition: box-shadow 0.2s;
}

/* Extra shadow after user scrolls */
.kq-nav--scrolled {
  box-shadow: 0 4px 18px rgba(26, 60, 110, 0.10);
}

/* Auth pages: no shadow so it doesn't compete with the dark left panel */
.kq-nav--auth {
  background: var(--kinetic-white);
  border-bottom-color: transparent;
  box-shadow: none;
}

/* ── Inner flex row (landing + auth) ─────────────────────────── */
.kq-nav-inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 68px;
  gap: 24px;
}

/* ── Inner flex row (app variant) ────────────────────────────────
   Full-width (no max-width cap) to span the content area that
   sits beside the sidebar. Keeps logo left, profile right.       */
.kq-nav-inner--app {
  max-width: none;             /* fill the full topbar width         */
  padding: 0 20px;             /* matches original MainLayout topbar */
}

/* ════════════════════════════════════════════════════════════
   APP VARIANT TOPBAR
   Unified to 68px — same as landing/auth so the logo reads
   at the same visual size on every page.
   ════════════════════════════════════════════════════════════ */
.kq-nav--app {
  height: 68px;                /* unified with landing/auth navbar  */
  background: var(--kinetic-white);
  border-bottom: 1px solid var(--kinetic-border);
  box-shadow: 0 2px 12px rgba(26, 60, 110, 0.06);
}

.kq-nav--app .kq-nav-inner--app {
  height: 68px;
}

/* ════════════════════════════════════════════════════════════
   LOGO — shared across all variants
   ════════════════════════════════════════════════════════════ */
.kq-nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--kinetic-blue);
  letter-spacing: -0.02em;
  white-space: nowrap;
  flex-shrink: 0;
  text-decoration: none;
}

/* Landing / auth logo: 54px — proportional inside 68px navbar */
.kq-nav-logo-img {
  height: 54px;
  width: auto;
  object-fit: contain;
  display: block;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
}

/* App variant logo: same 54px — visually identical to login/landing */
.kq-nav--app .kq-nav-logo-img {
  height: 54px;
}

/* ════════════════════════════════════════════════════════════
   CENTRE LINKS — landing page only
   ════════════════════════════════════════════════════════════ */
.kq-nav-links {
  display: flex;
  align-items: center;
  gap: 32px;
  list-style: none;
  flex: 1 1;
  justify-content: center;
}

.kq-nav-links a {
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--kinetic-gray);
  text-decoration: none;
  transition: color 0.2s;
  white-space: nowrap;
}

.kq-nav-links a:hover {
  color: var(--kinetic-teal);
}

.kq-nav-links a.active {
  color: var(--kinetic-blue);
  font-weight: 600;
}

/* ════════════════════════════════════════════════════════════
   RIGHT-SIDE ACTIONS (landing + auth)
   ════════════════════════════════════════════════════════════ */
.kq-nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* "Launch App" CTA */
.kq-nav-cta {
  background: var(--kinetic-teal);
  color: var(--kinetic-white) !important;
  padding: 9px 22px;
  border-radius: var(--radius-sm);
  font-weight: 600 !important;
  font-size: 0.88rem !important;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s, transform 0.15s !important;
  display: inline-block;
}

.kq-nav-cta:hover {
  background: var(--kinetic-blue) !important;
  transform: translateY(-1px);
}

/* Ghost link — login / register pages */
.kq-nav-ghost {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--kinetic-teal);
  text-decoration: none;
  padding: 8px 14px;
  border-radius: var(--radius-sm);
  border: 1.5px solid var(--kinetic-border);
  transition: border-color 0.2s, color 0.2s, background 0.2s;
  white-space: nowrap;
}

.kq-nav-ghost:hover {
  border-color: var(--kinetic-teal);
  background: var(--kinetic-light);
  color: var(--kinetic-blue);
}

/* ════════════════════════════════════════════════════════════
   PROFILE DROPDOWN — app / dashboard variant
   Ported from MainLayout.css (.profile-dropdown, .dropdown-*)
   and namespaced under .kq-nav-profile* to avoid collisions.
   ════════════════════════════════════════════════════════════ */

/* Wrapper — positions the dropdown panel relative to the trigger */
.kq-nav-profile {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  -webkit-user-select: none;
          user-select: none;
}

/* Trigger row: avatar icon + name */
.kq-nav-profile-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: var(--radius-sm);
  transition: background 0.15s;
}

.kq-nav-profile-trigger:hover {
  background: rgba(26, 60, 110, 0.06);
}

.kq-nav-profile-name {
  font-weight: 600;
  font-size: 14px;
  color: var(--kinetic-blue);
  font-family: var(--font-body);
}

/* Dropdown panel */
.kq-nav-profile-dropdown {
  position: absolute;
  top: calc(100% + 10px);   /* gap below the trigger */
  right: 0;
  background: var(--kinetic-white);
  padding: 12px;
  border-radius: var(--radius-md);
  box-shadow: 0 4px 16px rgba(26, 60, 110, 0.14);
  border: 1px solid var(--kinetic-border);
  z-index: 200;
  min-width: 190px;
  /* Subtle entrance animation */
  animation: kq-dropdown-in 0.15s ease;
}

@keyframes kq-dropdown-in {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0);    }
}

/* Email line */
.kq-nav-profile-email {
  font-size: 13px;
  color: var(--kinetic-gray);
  margin: 0 0 8px;
  word-break: break-all;
}

/* Divider */
.kq-nav-profile-divider {
  border: none;
  border-top: 1px solid var(--kinetic-border);
  margin: 0 0 8px;
}

/* Menu items */
.kq-nav-profile-item {
  display: block;
  width: 100%;
  padding: 8px 10px;
  text-align: left;
  border: none;
  background: none;
  font-size: 14px;
  font-family: var(--font-body);
  color: #333;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: background 0.15s, color 0.15s;
}

.kq-nav-profile-item:hover {
  background: var(--kinetic-light);
  color: var(--kinetic-blue);
}

/* Logout item — subtle red tint on hover */
.kq-nav-profile-item--logout:hover {
  background: #fff0f0;
  color: #c0392b;
}

/* ════════════════════════════════════════════════════════════
   GLOBAL SCROLL PREVENTION
   html/body must not scroll; MainLayout's pageContent div is
   the single scroll container for all app page content.
   ════════════════════════════════════════════════════════════ */
html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

#root {
  height: 100%;
  overflow: hidden;
}

/* ════════════════════════════════════════════════════════════
   RESPONSIVE
   ════════════════════════════════════════════════════════════ */
@media (max-width: 900px) {
  .kq-nav-links {
    gap: 20px;
  }
  .kq-nav-links a {
    font-size: 0.85rem;
  }
}

@media (max-width: 680px) {
  .kq-nav-inner {
    padding: 0 16px;
  }
  .kq-nav-links {
    display: none;
  }
  .kq-nav-logo-img {
    height: 44px;
  }
  .kq-nav--app .kq-nav-logo-img {
    height: 44px;
  }
  .kq-nav-cta {
    padding: 8px 16px;
    font-size: 0.83rem !important;
  }
  .kq-nav-ghost {
    font-size: 0.82rem;
    padding: 7px 10px;
  }
  .kq-nav-profile-name {
    display: none;   /* show icon only on small screens */
  }
}`, "",{"version":3,"sources":["webpack://./src/styles/Navbar.css"],"names":[],"mappings":"AAAA;;;;;;iEAMiE;;AAEjE,iEAAiE;AACjE;EACE,yBAAyB;EACzB,yBAAyB;EACzB,yBAAyB;EACzB,yBAAyB;EACzB,yBAAyB;EACzB,yBAAyB;EACzB,yBAAyB;EACzB,yBAAyB;;EAEzB,4CAA4C;;EAE5C,gBAAgB;EAChB,iBAAiB;EACjB,iBAAiB;AACnB;;AAEA;;;;iEAIiE;AACjE;EACE,eAAe;EACf,MAAM;EACN,OAAO;EACP,QAAQ;EACR,aAAa;EACb,gCAAgC;EAChC,8CAA8C;EAC9C,8CAA8C;EAC9C,6BAA6B;EAC7B,2BAA2B;AAC7B;;AAEA,oCAAoC;AACpC;EACE,8CAA8C;AAChD;;AAEA,yEAAyE;AACzE;EACE,gCAAgC;EAChC,gCAAgC;EAChC,gBAAgB;AAClB;;AAEA,mEAAmE;AACnE;EACE,iBAAiB;EACjB,cAAc;EACd,eAAe;EACf,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,YAAY;EACZ,SAAS;AACX;;AAEA;;mEAEmE;AACnE;EACE,eAAe,cAAc,uCAAuC;EACpE,eAAe,cAAc,uCAAuC;AACtE;;AAEA;;;;iEAIiE;AACjE;EACE,YAAY,iBAAiB,sCAAsC;EACnE,gCAAgC;EAChC,8CAA8C;EAC9C,8CAA8C;AAChD;;AAEA;EACE,YAAY;AACd;;AAEA;;iEAEiE;AACjE;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,uBAAuB;EACvB,mBAAmB;EACnB,cAAc;EACd,qBAAqB;AACvB;;AAEA,gEAAgE;AAChE;EACE,YAAY;EACZ,WAAW;EACX,mBAAmB;EACnB,cAAc;EACd,YAAY;EACZ,aAAa;EACb,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA,sEAAsE;AACtE;EACE,YAAY;AACd;;AAEA;;iEAEiE;AACjE;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,gBAAgB;EAChB,SAAO;EACP,uBAAuB;AACzB;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,qBAAqB;EACrB,sBAAsB;EACtB,mBAAmB;AACrB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,0BAA0B;EAC1B,gBAAgB;AAClB;;AAEA;;iEAEiE;AACjE;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,cAAc;AAChB;;AAEA,qBAAqB;AACrB;EACE,+BAA+B;EAC/B,sCAAsC;EACtC,iBAAiB;EACjB,+BAA+B;EAC/B,2BAA2B;EAC3B,6BAA6B;EAC7B,qBAAqB;EACrB,mBAAmB;EACnB,uDAAuD;EACvD,qBAAqB;AACvB;;AAEA;EACE,0CAA0C;EAC1C,2BAA2B;AAC7B;;AAEA,wCAAwC;AACxC;EACE,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;EAC1B,qBAAqB;EACrB,iBAAiB;EACjB,+BAA+B;EAC/B,yCAAyC;EACzC,0DAA0D;EAC1D,mBAAmB;AACrB;;AAEA;EACE,iCAAiC;EACjC,gCAAgC;EAChC,0BAA0B;AAC5B;;AAEA;;;;iEAIiE;;AAEjE,mEAAmE;AACnE;EACE,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,yBAAiB;UAAjB,iBAAiB;AACnB;;AAEA,oCAAoC;AACpC;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,gBAAgB;EAChB,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,mCAAmC;AACrC;;AAEA;EACE,gBAAgB;EAChB,eAAe;EACf,0BAA0B;EAC1B,6BAA6B;AAC/B;;AAEA,mBAAmB;AACnB;EACE,kBAAkB;EAClB,sBAAsB,IAAI,0BAA0B;EACpD,QAAQ;EACR,gCAAgC;EAChC,aAAa;EACb,+BAA+B;EAC/B,8CAA8C;EAC9C,uCAAuC;EACvC,YAAY;EACZ,gBAAgB;EAChB,8BAA8B;EAC9B,oCAAoC;AACtC;;AAEA;EACE,OAAO,UAAU,EAAE,2BAA2B,EAAE;EAChD,OAAO,UAAU,EAAE,wBAAwB,KAAK;AAClD;;AAEA,eAAe;AACf;EACE,eAAe;EACf,0BAA0B;EAC1B,eAAe;EACf,qBAAqB;AACvB;;AAEA,YAAY;AACZ;EACE,YAAY;EACZ,2CAA2C;EAC3C,eAAe;AACjB;;AAEA,eAAe;AACf;EACE,cAAc;EACd,WAAW;EACX,iBAAiB;EACjB,gBAAgB;EAChB,YAAY;EACZ,gBAAgB;EAChB,eAAe;EACf,6BAA6B;EAC7B,WAAW;EACX,eAAe;EACf,+BAA+B;EAC/B,yCAAyC;AAC3C;;AAEA;EACE,gCAAgC;EAChC,0BAA0B;AAC5B;;AAEA,2CAA2C;AAC3C;EACE,mBAAmB;EACnB,cAAc;AAChB;;AAEA;;;;iEAIiE;AACjE;;EAEE,YAAY;EACZ,SAAS;EACT,UAAU;EACV,gBAAgB;AAClB;;AAEA;EACE,YAAY;EACZ,gBAAgB;AAClB;;AAEA;;iEAEiE;AACjE;EACE;IACE,SAAS;EACX;EACA;IACE,kBAAkB;EACpB;AACF;;AAEA;EACE;IACE,eAAe;EACjB;EACA;IACE,aAAa;EACf;EACA;IACE,YAAY;EACd;EACA;IACE,YAAY;EACd;EACA;IACE,iBAAiB;IACjB,6BAA6B;EAC/B;EACA;IACE,kBAAkB;IAClB,iBAAiB;EACnB;EACA;IACE,aAAa,IAAI,oCAAoC;EACvD;AACF","sourcesContent":["/* ============================================================\n   Navbar.css — Shared KinetiQ Navigation Bar\n   Covers all page variants:\n     • Landing page  (full nav links + Launch App CTA)\n     • Auth pages    (login / register — logo + ghost link)\n     • App / Dashboard (logo + profile dropdown — replaces MainLayout topbar)\n   ============================================================ */\n\n/* ── Design tokens ─────────────────────────────────────────── */\n:root {\n  --kinetic-blue:   #1A3C6E;\n  --kinetic-teal:   #0B8DA6;\n  --kinetic-accent: #00C2A8;\n  --kinetic-light:  #F0F7FA;\n  --kinetic-white:  #FFFFFF;\n  --kinetic-dark:   #0D1F35;\n  --kinetic-gray:   #6B7A8D;\n  --kinetic-border: #D6E4EC;\n\n  --font-body: 'Inter', 'Segoe UI', sans-serif;\n\n  --radius-sm: 6px;\n  --radius-md: 12px;\n  --radius-lg: 20px;\n}\n\n/* ════════════════════════════════════════════════════════════\n   BASE NAV\n   position:fixed is used (not sticky) because auth pages set\n   overflow:hidden on ancestor elements, which breaks sticky.\n   ════════════════════════════════════════════════════════════ */\n.kq-nav {\n  position: fixed;\n  top: 0;\n  left: 0;\n  right: 0;\n  z-index: 1000;\n  background: var(--kinetic-white);\n  border-bottom: 1px solid var(--kinetic-border);\n  box-shadow: 0 2px 12px rgba(26, 60, 110, 0.06);\n  font-family: var(--font-body);\n  transition: box-shadow 0.2s;\n}\n\n/* Extra shadow after user scrolls */\n.kq-nav--scrolled {\n  box-shadow: 0 4px 18px rgba(26, 60, 110, 0.10);\n}\n\n/* Auth pages: no shadow so it doesn't compete with the dark left panel */\n.kq-nav--auth {\n  background: var(--kinetic-white);\n  border-bottom-color: transparent;\n  box-shadow: none;\n}\n\n/* ── Inner flex row (landing + auth) ─────────────────────────── */\n.kq-nav-inner {\n  max-width: 1140px;\n  margin: 0 auto;\n  padding: 0 24px;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  height: 68px;\n  gap: 24px;\n}\n\n/* ── Inner flex row (app variant) ────────────────────────────────\n   Full-width (no max-width cap) to span the content area that\n   sits beside the sidebar. Keeps logo left, profile right.       */\n.kq-nav-inner--app {\n  max-width: none;             /* fill the full topbar width         */\n  padding: 0 20px;             /* matches original MainLayout topbar */\n}\n\n/* ════════════════════════════════════════════════════════════\n   APP VARIANT TOPBAR\n   Unified to 68px — same as landing/auth so the logo reads\n   at the same visual size on every page.\n   ════════════════════════════════════════════════════════════ */\n.kq-nav--app {\n  height: 68px;                /* unified with landing/auth navbar  */\n  background: var(--kinetic-white);\n  border-bottom: 1px solid var(--kinetic-border);\n  box-shadow: 0 2px 12px rgba(26, 60, 110, 0.06);\n}\n\n.kq-nav--app .kq-nav-inner--app {\n  height: 68px;\n}\n\n/* ════════════════════════════════════════════════════════════\n   LOGO — shared across all variants\n   ════════════════════════════════════════════════════════════ */\n.kq-nav-logo {\n  display: flex;\n  align-items: center;\n  gap: 10px;\n  font-size: 1.35rem;\n  font-weight: 800;\n  color: var(--kinetic-blue);\n  letter-spacing: -0.02em;\n  white-space: nowrap;\n  flex-shrink: 0;\n  text-decoration: none;\n}\n\n/* Landing / auth logo: 54px — proportional inside 68px navbar */\n.kq-nav-logo-img {\n  height: 54px;\n  width: auto;\n  object-fit: contain;\n  display: block;\n  border: none;\n  outline: none;\n  box-shadow: none;\n  background: transparent;\n}\n\n/* App variant logo: same 54px — visually identical to login/landing */\n.kq-nav--app .kq-nav-logo-img {\n  height: 54px;\n}\n\n/* ════════════════════════════════════════════════════════════\n   CENTRE LINKS — landing page only\n   ════════════════════════════════════════════════════════════ */\n.kq-nav-links {\n  display: flex;\n  align-items: center;\n  gap: 32px;\n  list-style: none;\n  flex: 1;\n  justify-content: center;\n}\n\n.kq-nav-links a {\n  font-size: 0.92rem;\n  font-weight: 500;\n  color: var(--kinetic-gray);\n  text-decoration: none;\n  transition: color 0.2s;\n  white-space: nowrap;\n}\n\n.kq-nav-links a:hover {\n  color: var(--kinetic-teal);\n}\n\n.kq-nav-links a.active {\n  color: var(--kinetic-blue);\n  font-weight: 600;\n}\n\n/* ════════════════════════════════════════════════════════════\n   RIGHT-SIDE ACTIONS (landing + auth)\n   ════════════════════════════════════════════════════════════ */\n.kq-nav-actions {\n  display: flex;\n  align-items: center;\n  gap: 12px;\n  flex-shrink: 0;\n}\n\n/* \"Launch App\" CTA */\n.kq-nav-cta {\n  background: var(--kinetic-teal);\n  color: var(--kinetic-white) !important;\n  padding: 9px 22px;\n  border-radius: var(--radius-sm);\n  font-weight: 600 !important;\n  font-size: 0.88rem !important;\n  text-decoration: none;\n  white-space: nowrap;\n  transition: background 0.2s, transform 0.15s !important;\n  display: inline-block;\n}\n\n.kq-nav-cta:hover {\n  background: var(--kinetic-blue) !important;\n  transform: translateY(-1px);\n}\n\n/* Ghost link — login / register pages */\n.kq-nav-ghost {\n  font-size: 0.88rem;\n  font-weight: 500;\n  color: var(--kinetic-teal);\n  text-decoration: none;\n  padding: 8px 14px;\n  border-radius: var(--radius-sm);\n  border: 1.5px solid var(--kinetic-border);\n  transition: border-color 0.2s, color 0.2s, background 0.2s;\n  white-space: nowrap;\n}\n\n.kq-nav-ghost:hover {\n  border-color: var(--kinetic-teal);\n  background: var(--kinetic-light);\n  color: var(--kinetic-blue);\n}\n\n/* ════════════════════════════════════════════════════════════\n   PROFILE DROPDOWN — app / dashboard variant\n   Ported from MainLayout.css (.profile-dropdown, .dropdown-*)\n   and namespaced under .kq-nav-profile* to avoid collisions.\n   ════════════════════════════════════════════════════════════ */\n\n/* Wrapper — positions the dropdown panel relative to the trigger */\n.kq-nav-profile {\n  position: relative;\n  display: flex;\n  align-items: center;\n  cursor: pointer;\n  user-select: none;\n}\n\n/* Trigger row: avatar icon + name */\n.kq-nav-profile-trigger {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  padding: 5px 8px;\n  border-radius: var(--radius-sm);\n  transition: background 0.15s;\n}\n\n.kq-nav-profile-trigger:hover {\n  background: rgba(26, 60, 110, 0.06);\n}\n\n.kq-nav-profile-name {\n  font-weight: 600;\n  font-size: 14px;\n  color: var(--kinetic-blue);\n  font-family: var(--font-body);\n}\n\n/* Dropdown panel */\n.kq-nav-profile-dropdown {\n  position: absolute;\n  top: calc(100% + 10px);   /* gap below the trigger */\n  right: 0;\n  background: var(--kinetic-white);\n  padding: 12px;\n  border-radius: var(--radius-md);\n  box-shadow: 0 4px 16px rgba(26, 60, 110, 0.14);\n  border: 1px solid var(--kinetic-border);\n  z-index: 200;\n  min-width: 190px;\n  /* Subtle entrance animation */\n  animation: kq-dropdown-in 0.15s ease;\n}\n\n@keyframes kq-dropdown-in {\n  from { opacity: 0; transform: translateY(-6px); }\n  to   { opacity: 1; transform: translateY(0);    }\n}\n\n/* Email line */\n.kq-nav-profile-email {\n  font-size: 13px;\n  color: var(--kinetic-gray);\n  margin: 0 0 8px;\n  word-break: break-all;\n}\n\n/* Divider */\n.kq-nav-profile-divider {\n  border: none;\n  border-top: 1px solid var(--kinetic-border);\n  margin: 0 0 8px;\n}\n\n/* Menu items */\n.kq-nav-profile-item {\n  display: block;\n  width: 100%;\n  padding: 8px 10px;\n  text-align: left;\n  border: none;\n  background: none;\n  font-size: 14px;\n  font-family: var(--font-body);\n  color: #333;\n  cursor: pointer;\n  border-radius: var(--radius-sm);\n  transition: background 0.15s, color 0.15s;\n}\n\n.kq-nav-profile-item:hover {\n  background: var(--kinetic-light);\n  color: var(--kinetic-blue);\n}\n\n/* Logout item — subtle red tint on hover */\n.kq-nav-profile-item--logout:hover {\n  background: #fff0f0;\n  color: #c0392b;\n}\n\n/* ════════════════════════════════════════════════════════════\n   GLOBAL SCROLL PREVENTION\n   html/body must not scroll; MainLayout's pageContent div is\n   the single scroll container for all app page content.\n   ════════════════════════════════════════════════════════════ */\nhtml,\nbody {\n  height: 100%;\n  margin: 0;\n  padding: 0;\n  overflow: hidden;\n}\n\n#root {\n  height: 100%;\n  overflow: hidden;\n}\n\n/* ════════════════════════════════════════════════════════════\n   RESPONSIVE\n   ════════════════════════════════════════════════════════════ */\n@media (max-width: 900px) {\n  .kq-nav-links {\n    gap: 20px;\n  }\n  .kq-nav-links a {\n    font-size: 0.85rem;\n  }\n}\n\n@media (max-width: 680px) {\n  .kq-nav-inner {\n    padding: 0 16px;\n  }\n  .kq-nav-links {\n    display: none;\n  }\n  .kq-nav-logo-img {\n    height: 44px;\n  }\n  .kq-nav--app .kq-nav-logo-img {\n    height: 44px;\n  }\n  .kq-nav-cta {\n    padding: 8px 16px;\n    font-size: 0.83rem !important;\n  }\n  .kq-nav-ghost {\n    font-size: 0.82rem;\n    padding: 7px 10px;\n  }\n  .kq-nav-profile-name {\n    display: none;   /* show icon only on small screens */\n  }\n}"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
